import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { faClockRotateLeft, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";

import { crmPostventaFichaApi } from "../../crmPostventaFichaApi";
import { ORIGEN_LABELS, TIPOS_CAMPO } from "../../fichaFormat";
import { todayIso } from "../../../crmpostventa-shared/crmFormat";
import useIsMobile from "../../../crmpostventa-shared/useIsMobile";
import Modal from "../../../crmpostventa-shared/components/Modal";
import EmptyState, { LoadError } from "../../../crmpostventa-shared/components/EmptyState";
import { DateRange, FilterSelect } from "../../../crmpostventa-shared/components/FilterFields";
import HistorialCambios from "../HistorialCambios";
import "../../../crmpostventa-rollo/rollo.css";
import "../../ficha.css";
import "./historial.css";

// Entries per page of GET {clienteId}/historial ("Cargar más" asks for the next one; the server allows up to 200).
export const TAMANO_PAGINA = 20;

const TITLE_ID = "crm-ficha-hpanel-title";

const EMPTY_FILTERS = { seccion: "", campo: "", usuarioId: "", origen: "", desde: "", hasta: "" };

const ORIGEN_OPTIONS = Object.entries(ORIGEN_LABELS).map(([value, label]) => ({ value, label }));

const SKELETON = [0, 1, 2];

// Query of one page: only the filters that are set (usuarioId as a number).
const buildQuery = (filters, pagina) => {
  const query = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== "") query[key] = key === "usuarioId" ? Number(value) : value;
  });
  return { ...query, pagina, tamanoPagina: TAMANO_PAGINA };
};

const editablesOf = (seccion) =>
  (seccion?.campos || []).filter((campo) => campo.tipo === TIPOS_CAMPO.EDITABLE).map((campo) => ({ value: campo.clave, label: campo.etiqueta }));

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

function HistorialSkeleton() {
  return (
    <div className="crm-ficha__hskel" aria-busy="true" aria-label="Cargando el historial">
      {SKELETON.map((row) => (
        <div key={row} className="crm-ficha__hskel-card">
          <span className="crm-rollo__skel" style={{ width: "45%", height: "12px" }} aria-hidden="true" />
          <span className="crm-rollo__skel" style={{ width: "70%" }} aria-hidden="true" />
          <span className="crm-rollo__skel" style={{ width: "100%", height: "56px" }} aria-hidden="true" />
        </div>
      ))}
    </div>
  );
}

function HistorialDialog({ clienteId, terminalLabel, comercio, secciones, seccionInicial, darkMode, returnFocusRef, onClose, onNavigate }) {
  const isMobile = useIsMobile();
  const [filters, setFilters] = useState(() => ({ ...EMPTY_FILTERS, seccion: seccionInicial || "" }));
  const [list, setList] = useState({ status: "loading", items: [], total: 0, pagina: 0, asesores: [] });
  const [more, setMore] = useState({ loading: false, error: null });
  const requestRef = useRef(0);
  const bodyRef = useRef(null);

  const seccionList = useMemo(() => (Array.isArray(secciones) ? secciones : []), [secciones]);

  const titulos = useMemo(() => {
    const map = new Map(seccionList.map((seccion) => [seccion.clave, seccion.titulo]));
    return (clave) => map.get(clave) || "";
  }, [seccionList]);

  // One request at a time: a newer one (filters changed, retry, close) makes every older answer stale.
  const fetchPage = useCallback(
    async (pagina) => {
      requestRef.current += 1;
      const request = requestRef.current;
      if (pagina === 1) setList((current) => ({ ...current, status: "loading", items: [], total: 0, pagina: 0 }));
      setMore({ loading: pagina > 1, error: null });
      try {
        const response = await crmPostventaFichaApi.obtenerHistorial(clienteId, buildQuery(filters, pagina));
        if (request !== requestRef.current) return;
        const items = Array.isArray(response?.items) ? response.items : [];
        setList((current) => ({
          status: "ready",
          items: pagina === 1 ? items : [...current.items, ...items],
          total: Number(response?.total) || 0,
          pagina,
          asesores: Array.isArray(response?.asesores) ? response.asesores : current.asesores,
        }));
        setMore({ loading: false, error: null });
      } catch (error) {
        if (request !== requestRef.current) return;
        if (pagina === 1) {
          setList((current) => ({ ...current, status: "error" }));
          setMore({ loading: false, error: null });
        } else {
          setMore({ loading: false, error });
        }
      }
    },
    [clienteId, filters]
  );

  useEffect(() => {
    fetchPage(1);
    return () => {
      requestRef.current += 1;
    };
  }, [fetchPage]);

  useEffect(() => {
    bodyRef.current?.closest('[role="dialog"]')?.focus();
  }, []);

  const handleFilter = (name, value) =>
    setFilters((current) => {
      const next = { ...current, [name]: value };
      if (name === "seccion" && value && current.campo) {
        const seccion = seccionList.find((item) => item.clave === value);
        if (!editablesOf(seccion).some((option) => option.value === current.campo)) next.campo = "";
      }
      return next;
    });

  const hasFilters = Object.values(filters).some((value) => value !== "");
  const clearFilters = () => setFilters(EMPTY_FILTERS);

  const seccionOptions = seccionList.map((seccion) => ({ value: seccion.clave, label: seccion.titulo }));
  const campoSelect = filters.seccion ? (
    <FilterSelect
      name="campo"
      label="Campo"
      value={filters.campo}
      options={editablesOf(seccionList.find((seccion) => seccion.clave === filters.seccion))}
      onChange={handleFilter}
    />
  ) : (
    <FilterSelect
      name="campo"
      label="Campo"
      value={filters.campo}
      groups={seccionList
        .map((seccion) => ({ label: seccion.titulo, options: editablesOf(seccion) }))
        .filter((group) => group.options.length)}
      onChange={handleFilter}
    />
  );
  const asesorOptions = list.asesores.map((asesor) => ({ value: String(asesor.id), label: asesor.nombre }));

  const renderList = () => {
    if (list.status === "loading") return <HistorialSkeleton />;
    if (list.status === "error") {
      return <LoadError title="No pudimos cargar el historial" onRetry={() => fetchPage(1)} />;
    }
    if (!list.items.length) {
      return hasFilters ? (
        <EmptyState
          icon={faMagnifyingGlass}
          title="No hay cambios con estos filtros"
          text="Probá con otra sección, campo, asesor, origen o fecha."
          action={
            <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary" onClick={clearFilters}>
              Limpiar filtros
            </button>
          }
        />
      ) : (
        <EmptyState
          icon={faClockRotateLeft}
          title="Todavía no hay cambios registrados en esta terminal"
          text="Cuando alguien modifique sus datos, cada campo queda acá con quién, cuándo, el motivo y de dónde vino."
        />
      );
    }

    const hasMore = list.items.length < list.total;
    return (
      <>
        <div className="crm-ficha__hmeta">{`${plural(list.total, "cambio", "cambios")} · más reciente primero`}</div>
        <div aria-busy={more.loading ? "true" : undefined}>
          <HistorialCambios
            items={list.items}
            isMobile={isMobile}
            tituloDe={titulos}
            clienteId={clienteId}
            today={todayIso()}
            onNavigate={onNavigate}
          />
        </div>
        {more.error ? <LoadError title="No pudimos cargar más cambios" onRetry={() => fetchPage(list.pagina + 1)} /> : null}
        <div className="crm-ficha__hmore">
          <span>{`Mostrando ${list.items.length} de ${list.total}`}</span>
          {hasMore && !more.error ? (
            <button
              type="button"
              className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm"
              disabled={more.loading}
              aria-busy={more.loading ? "true" : undefined}
              onClick={() => fetchPage(list.pagina + 1)}
            >
              {more.loading ? "Cargando más…" : "Cargar más"}
            </button>
          ) : null}
        </div>
      </>
    );
  };

  return (
    <Modal
      titleId={TITLE_ID}
      title={`Historial de cambios · ${terminalLabel || ""}`.trim()}
      size="drawer"
      darkMode={darkMode}
      returnFocusRef={returnFocusRef}
      onClose={onClose}
      className={`crm-ficha crm-ficha-hpanel${isMobile ? " is-mobile" : ""}`}
      headExtra={comercio ? <p className="crm-rollo__desc">{comercio}</p> : null}
      bodyClassName="crm-ficha__hbody"
    >
      <div ref={bodyRef} className="crm-rollo__filters crm-ficha__hfilters">
        <FilterSelect name="seccion" label="Sección" value={filters.seccion} options={seccionOptions} onChange={handleFilter} />
        {campoSelect}
        <FilterSelect name="usuarioId" label="Asesor" value={filters.usuarioId} options={asesorOptions} onChange={handleFilter} />
        <FilterSelect name="origen" label="Origen" value={filters.origen} options={ORIGEN_OPTIONS} onChange={handleFilter} />
        <DateRange label="Fecha" fromKey="desde" toKey="hasta" filters={filters} today={todayIso()} onChange={handleFilter} />
        <button type="button" className="crm-rollo__link" disabled={!hasFilters} onClick={clearFilters}>
          Limpiar filtros
        </button>
      </div>
      {renderList()}
    </Modal>
  );
}

/**
 * Full change history of one terminal (T8 of odd/tasks/ficha-cliente.md, S10–S11; design Prompt 4): a large side panel
 * (full screen on mobile) over GET api/crm-postventa-ficha/{clienteId}/historial, with the FilterBar (Sección, Campo with
 * only the editable fields, Asesor from the response, Origen, fechas), the changes newest first and "Cargar más".
 *
 * The container keeps `open`: the panel mounts fresh on each opening, filtered by seccionInicial (a card footer passes
 * its block), and closing (Escape, the cross, the scrim) calls onClose; the focus goes back to the opener or to
 * returnFocusRef. secciones is the ficha's secciones list (Campo options and block titles). onNavigate(href) receives
 * the "Ver gestión" link.
 */
export default function HistorialCompletoPanel({ open, ...props }) {
  return open ? <HistorialDialog {...props} /> : null;
}
